'use client';

import { useGetCrmObjectsQuery } from 'src/api/redux';
import { Controller, useFormContext } from 'react-hook-form';
import ControlLabel from 'src/components/ControlledFields/ControlLabel';
import CrmOptionItem from './CrmOptionItem';
import { useState, useMemo, useRef, useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faSpinner } from '@fortawesome/pro-solid-svg-icons';
import { twMerge } from 'tailwind-merge';

interface ControlledCrmObjectSelectProps {
  name: string;
  label?: string;
  options?: any[];
  required?: boolean;
  rules?: any;
  placeholder?: string;
  loading?: boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  helperText?: string;
  hasTooltip?: boolean;
  tooltipText?: string;
  size?: 'small' | 'medium';
  disabled?: boolean;
  writableOnly?: boolean;
  crmType: string;
  integration: string;
  showLabel?: boolean;
}

// This is also called Destination field
const ControlledCrmObjectSelect = ({
  ...props
}: ControlledCrmObjectSelectProps) => {
  const [activeItem, setActiveItem] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext();

  const isThirdParty = props.integration === 'third_party';

  const {
    data: crmObjects,
    isLoading,
    isFetching,
  } = useGetCrmObjectsQuery(
    {
      crmType: props.crmType,
      crm: props.integration,
      writable_only: props?.writableOnly || false,
    },
    {
      skip: isThirdParty || !props.crmType || !props.integration,
    }
  );

  // sort options by required first and then alphabetically
  const orderedOptions = useMemo(() => {
    if (!crmObjects) return [];
    return crmObjects.slice().sort((a, b) => {
      if (a.required && !b.required) return -1;
      if (!a.required && b.required) return 1;
      if (a.label < b.label) return -1;
      if (a.label > b.label) return 1;
      return 0;
    });
  }, [crmObjects]);

  const filteredOptions = useMemo(() => {
    if (!searchValue) return orderedOptions;
    return orderedOptions.filter((option) =>
      option.label.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [orderedOptions, searchValue]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  if (!props) return null;
  const errorMessage = props?.helperText || 'This field is required';
  const required = props?.required || props.rules?.required;
  const name = props?.name;
  const loading = isLoading || isFetching;

  return (
    <Controller
      name={name}
      control={control}
      rules={{
        required: props.required ? errorMessage : false,
        ...props.rules,
      }}
      render={({ field }) => {
        const selectedOption = orderedOptions.find(
          (option) => option.code === field.value
        );

        return (
          <ControlLabel
            label={props?.label || ''}
            required={required}
            hasTooltip={props.hasTooltip}
            tooltipText={props.tooltipText}
            showLabel={props.showLabel}
          >
            <div ref={containerRef} className="relative w-full">
              <div
                className={twMerge(
                  'flex items-center w-full px-3 bg-white border-b cursor-pointer transition-all',
                  props.size === 'small' ? 'min-h-8 py-1' : 'min-h-10 py-2',
                  isOpen
                    ? 'border-neutral-500'
                    : 'border-neutral-300 hover:border-neutral-500',
                  !!errors[props.name] && 'border-red-500'
                )}
                onClick={() => {
                  if (!props.disabled) {
                    setIsOpen(!isOpen);
                    if (!isOpen) {
                      setTimeout(() => inputRef.current?.focus(), 0);
                    }
                  }
                }}
              >
                <input
                  ref={inputRef}
                  type="text"
                  className={twMerge(
                    'flex-1 bg-transparent outline-none placeholder:text-neutral-500',
                    props.size === 'small' ? 'text-xs' : 'text-sm'
                  )}
                  placeholder={
                    selectedOption?.label || props.placeholder || 'Select...'
                  }
                  value={searchValue}
                  onChange={(e) => {
                    setSearchValue(e.target.value);
                    if (!isOpen) setIsOpen(true);
                  }}
                  onBlur={field.onBlur}
                  disabled={props.disabled}
                />
                {loading ? (
                  <Icon
                    icon={faSpinner}
                    className="w-3.5 h-3.5 text-neutral-500 animate-spin"
                  />
                ) : (
                  <Icon
                    icon={faCaretDown}
                    className={twMerge(
                      'w-3.5 h-3.5 text-neutral-500 transition-transform',
                      isOpen && '-rotate-90'
                    )}
                  />
                )}
              </div>

              {isOpen && !props.disabled && (
                <ul className="absolute z-50 w-full mt-1 py-1 bg-white border border-neutral-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {loading ? (
                    <li className="px-3 py-2 text-sm text-neutral-500 flex items-center gap-2">
                      <Icon icon={faSpinner} className="animate-spin" />
                      Loading...
                    </li>
                  ) : filteredOptions.length === 0 ? (
                    <li className="px-3 py-2 text-sm text-neutral-500">
                      No results found
                    </li>
                  ) : (
                    filteredOptions.map((option) => (
                      <CrmOptionItem
                        key={`territory-item-${option.code}`}
                        option={option}
                        activeItem={activeItem || watch(name)}
                        onClick={() => {
                          field.onChange(option.code);
                          setActiveItem(option);
                          setSearchValue('');
                          setIsOpen(false);
                        }}
                      />
                    ))
                  )}
                </ul>
              )}
            </div>
            {!!errors[props.name] && (
              <p className="text-red-500 text-xs mt-1">
                {errors[props.name]?.message as string}
              </p>
            )}
          </ControlLabel>
        );
      }}
    />
  );
};

export default ControlledCrmObjectSelect;
